import axios from "axios";
import { debug, formatSafeErrorMessage } from "../utils/debugLogger";
import type { SocialConnectionHealth } from "../types/socialConnections";

// Same social-management API base as socialTokenService.
const SOCIAL_API_BASE =
  process.env.REACT_APP_SOCIAL_API_URL ||
  "https://bc631mqtg1.execute-api.us-east-1.amazonaws.com/Prod";

/**
 * Own axios instance, deliberately not the global one. The app-wide interceptor
 * signs the user out on any 401/403 — but this is a background poll for the
 * alarm bell, and a 403 here (selected customer briefly not ours) or a transient
 * 401 must not end the session. Same reasoning as ghlOutreach / the Facebook
 * Graph client. Callers pass a freshly-resolved idToken (getFreshIdToken).
 */
const client = axios.create({ baseURL: SOCIAL_API_BASE, timeout: 10000 });

/**
 * Token health for every stored connection of a customer (today: LinkedIn).
 * Never throws — a failed call is reported as "no data" (empty list).
 */
export const getConnectionHealth = async (
  customerId: string,
  idToken: string
): Promise<SocialConnectionHealth[]> => {
  try {
    const response = await client.get(`/${customerId}/social/connections`, {
      headers: { Authorization: `Bearer ${idToken}` },
    });
    const connections = response.data?.connections;
    return Array.isArray(connections) ? connections : [];
  } catch (error: unknown) {
    debug.error("Error fetching connection health:", formatSafeErrorMessage(error));
    return [];
  }
};
